import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const formatDate = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleString('en-GB'); } catch { return String(value); }
};

const toInputValue = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60_000);
    return local.toISOString().slice(0, 16);
  } catch { return ''; }
};

const lifecycleBadge = (assignment) => {
  const now = Date.now();
  const release = new Date(assignment.releaseAt).getTime();
  const due = assignment.dueAt ? new Date(assignment.dueAt).getTime() : null;
  if (release > now) return { bg: 'secondary', label: 'Scheduled' };
  if (due && now > due) return { bg: 'danger', label: 'Past due' };
  return { bg: 'success', label: 'Active' };
};

const FacilitatorAssignments = ({ token, drills, onOpenResults, onBack }) => {
  const publishedDrills = useMemo(() => (drills || []).filter((d) => d.status === 'published'), [drills]);
  const [assignments, setAssignments] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | { ...assignment? } | { isNew: true }

  const refresh = async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const [assignmentsRes, cohortsRes] = await Promise.all([
        api.listExerciseAssignments(token),
        api.listCohorts(token).catch(() => ({ cohorts: [] })),
      ]);
      setAssignments(assignmentsRes.assignments || []);
      setCohorts(cohortsRes.cohorts || []);
    } catch (err) {
      setError(err?.message || 'Could not load assignments.');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    if (!publishedDrills.length) { setError('Publish at least one exercise before creating an assignment.'); return; }
    if (!cohorts.length) { setError('Create a cohort first (Facilitator admin → Student groups).'); return; }
    setEditing({
      isNew: true,
      drillId: publishedDrills[0]?.id || '',
      cohortId: cohorts[0]?.id || '',
      releaseAt: toInputValue(new Date()),
      dueAt: '',
      title: '',
      instructions: '',
    });
  };
  const openEdit = (a) => setEditing({
    isNew: false,
    id: a.id,
    drillId: a.drillId,
    cohortId: a.cohortId,
    releaseAt: toInputValue(a.releaseAt),
    dueAt: toInputValue(a.dueAt),
    title: a.title || '',
    instructions: a.instructions || '',
  });
  const closeEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const payload = {
      drillId: editing.drillId,
      cohortId: editing.cohortId,
      releaseAt: editing.releaseAt ? new Date(editing.releaseAt).toISOString() : new Date().toISOString(),
      dueAt: editing.dueAt ? new Date(editing.dueAt).toISOString() : null,
      title: editing.title,
      instructions: editing.instructions,
    };
    try {
      if (editing.isNew) {
        await api.createExerciseAssignment(payload, token);
      } else {
        await api.updateExerciseAssignment(editing.id, payload, token);
      }
      closeEdit();
      await refresh();
    } catch (err) {
      setError(err?.message || 'Could not save assignment.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this assignment? Students will lose access via this surface; their completed sessions remain in their portfolio.')) return;
    try {
      await api.deleteExerciseAssignment(id, token);
      await refresh();
    } catch (err) {
      setError(err?.message || 'Could not delete assignment.');
    }
  };

  return (
    <div className="student-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title="Assignments"
          description="Schedule an exercise to a cohort. Students see it from their home page once the release date passes; you can track completion and scores here."
          onBack={onBack}
          actions={<Button type="button" variant="primary" size="sm" onClick={openNew}>
            <i className="bi bi-plus-circle me-1" /> New assignment
          </Button>}
        />

        {error ? <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert> : null}

        {loading ? (
          <Alert variant="info">Loading…</Alert>
        ) : assignments.length === 0 ? (
          <Alert variant="info">No assignments yet. Click <strong>New assignment</strong> to schedule one.</Alert>
        ) : (
          <Card className="container-shadow">
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Exercise</th>
                  <th>Cohort</th>
                  <th>Release</th>
                  <th>Due</th>
                  <th>Completion</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => {
                  const badge = lifecycleBadge(a);
                  return (
                    <tr key={a.id}>
                      <td>
                        <div className="fw-semibold">{a.drillTitle}</div>
                        {a.title ? <div className="small text-muted">{a.title}</div> : null}
                      </td>
                      <td>{a.cohortName}</td>
                      <td className="small">{formatDate(a.releaseAt)}</td>
                      <td className="small">{a.dueAt ? formatDate(a.dueAt) : <span className="text-muted">No due date</span>}</td>
                      <td>{a.completedCount} / {a.cohortMemberCount}</td>
                      <td><Badge bg={badge.bg}>{badge.label}</Badge></td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end flex-wrap">
                          <Button size="sm" variant="outline-primary" onClick={() => onOpenResults?.(a)}>Results</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => openEdit(a)}>Edit</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => remove(a.id)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        )}
      </Container>

      <Modal show={Boolean(editing)} onHide={closeEdit} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing?.isNew ? 'Schedule a new assignment' : 'Edit assignment'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing ? (
            <>
              <Row className="g-3">
                <Form.Group as={Col} md={6}>
                  <Form.Label>Exercise</Form.Label>
                  <Form.Select
                    value={editing.drillId}
                    onChange={(e) => setEditing({ ...editing, drillId: e.target.value })}
                    disabled={!editing.isNew}
                  >
                    <option value="" disabled>Select an exercise…</option>
                    {publishedDrills.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                  </Form.Select>
                  {!editing.isNew ? <Form.Text className="text-muted">Exercise + cohort are fixed once an assignment exists. Delete and re-create to change.</Form.Text> : null}
                </Form.Group>
                <Form.Group as={Col} md={6}>
                  <Form.Label>Cohort</Form.Label>
                  <Form.Select
                    value={editing.cohortId}
                    onChange={(e) => setEditing({ ...editing, cohortId: e.target.value })}
                    disabled={!editing.isNew}
                  >
                    <option value="" disabled>Select a cohort…</option>
                    {(cohorts || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Row>
              <Row className="g-3 mt-1">
                <Form.Group as={Col} md={6}>
                  <Form.Label>Release date</Form.Label>
                  <Form.Control type="datetime-local"
                    value={editing.releaseAt || ''}
                    onChange={(e) => setEditing({ ...editing, releaseAt: e.target.value })} />
                  <Form.Text className="text-muted">Students see this on their home page from this date.</Form.Text>
                </Form.Group>
                <Form.Group as={Col} md={6}>
                  <Form.Label>Due date <span className="text-muted small">(optional)</span></Form.Label>
                  <Form.Control type="datetime-local"
                    value={editing.dueAt || ''}
                    onChange={(e) => setEditing({ ...editing, dueAt: e.target.value })} />
                  <Form.Text className="text-muted">Completed sessions after this date are flagged as late on the results table.</Form.Text>
                </Form.Group>
              </Row>
              <Row className="g-3 mt-1">
                <Form.Group as={Col} md={12}>
                  <Form.Label>Title shown to students <span className="text-muted small">(optional)</span></Form.Label>
                  <Form.Control
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="Week 1 - infusion rate practice" />
                </Form.Group>
                <Form.Group as={Col} md={12}>
                  <Form.Label>Instructions <span className="text-muted small">(optional)</span></Form.Label>
                  <Form.Control as="textarea" rows={3}
                    value={editing.instructions}
                    onChange={(e) => setEditing({ ...editing, instructions: e.target.value })}
                    placeholder="Complete by Friday. You can retake to improve your score." />
                </Form.Group>
              </Row>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={closeEdit}>Cancel</Button>
          <Button type="button" variant="primary" onClick={save} disabled={!editing?.drillId || !editing?.cohortId || !editing?.releaseAt}>
            {editing?.isNew ? 'Create assignment' : 'Save changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FacilitatorAssignments;
